// =============================================================================
// WeeklyUpdate/index.jsx
//
// Entry point for the Weekly Update module.
//
// Router compatibility:
//   routes.js calls: children: WeeklyUpdate()
//   This returns <WeeklyUpdateInner /> as a stable JSX element so React
//   maintains proper hook lifecycle across parent re-renders.
// =============================================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Stack, Tab, Tabs, Typography, Button,
    Alert, Snackbar, Chip, CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';

import WeeklyUpdateForm from './components/WeeklyUpdateForm';
import ManagerDashboard from './components/ManagerDashboard';
import SubmissionHistory from './components/SubmissionHistory';
import UserManagement from './components/UserManagement';
import {
    apiGetMyRole,
    apiGetCurrentWeek,
    apiGetMySubmission,
    apiGetDeadline,
    apiSave,
    apiDelete,
} from './requests/weeklyUpdateRequests';


// ─── Inner Component ────────────────────────────────────────────────────────

const WeeklyUpdateInner = () => {

    const [role, setRole]               = useState(null);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [submission, setSubmission]   = useState(null);
    const [deadline, setDeadline]       = useState(null);
    const [loadingInit, setLoadingInit] = useState(true);
    const [loadingSub, setLoadingSub]   = useState(false);
    const [saving, setSaving]           = useState(false);
    const [error, setError]             = useState(null);
    const [tab, setTab]                 = useState(0);
    const [snackbar, setSnackbar]       = useState({ open: false, message: '', severity: 'success' });
    const [historyOpen, setHistoryOpen] = useState(false);
    const [copiedData, setCopiedData]   = useState(null);

    const weekRef = useRef(null);
    const userId  = useSelector((s) => s.session.user.id);

    // ── Snackbar ─────────────────────────────────────────────────────────────
    const showSnack  = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });
    const closeSnack = () =>
        setSnackbar((prev) => ({ ...prev, open: false }));

    // ── Load submission ──────────────────────────────────────────────────────
    const loadSubmission = useCallback(async (weekNumber, year) => {
        setLoadingSub(true);
        setError(null);
        try {
            const [subRes, deadlineRes] = await Promise.all([
                apiGetMySubmission(weekNumber, year),
                apiGetDeadline(weekNumber, year),
            ]);
            setSubmission(subRes.data);
            setDeadline(deadlineRes.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load submission.');
        } finally {
            setLoadingSub(false);
        }
    }, []);

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const [roleRes, weekRes] = await Promise.all([
                    apiGetMyRole(),
                    apiGetCurrentWeek(),
                ]);

                const roleData = roleRes.data;
                const weekData = weekRes.data;

                setRole(roleData);
                setCurrentWeek(weekData);
                weekRef.current = weekData;
                setLoadingInit(false);

                // Only load submission if user has a WU role (content access)
                const hasWuRole = roleData?.wuRole?.role || roleData?.wuRole?.Role;
                if (hasWuRole && weekData) {
                    await loadSubmission(weekData.weekNumber, weekData.year);
                }
            } catch (err) {
                setError(err?.response?.data?.message || 'Failed to load module.');
                setLoadingInit(false);
            }
        };

        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async (formData) => {
        setSaving(true);
        setError(null);
        try {
            await apiSave(formData);
            setSaving(false);
            showSnack('Submission saved successfully.');
            const wk = weekRef.current;
            if (wk) await loadSubmission(wk.weekNumber, wk.year);
        } catch (err) {
            const message = err?.response?.data?.message || 'Failed to save submission.';
            setSaving(false);
            setError(message);
            showSnack(message, 'error');
        }
    }, [loadSubmission]);

    // ── Copy from history ─────────────────────────────────────────────────────
    const handleCopyFromHistory = useCallback((data) => {
        setCopiedData(data);
        setTab(0);
        showSnack('Data copied from history. Review and save when ready.', 'info');
    }, []);

    // ── Delete current submission ─────────────────────────────────────────────
    const handleDelete = useCallback(async () => {
        const wk = weekRef.current;
        if (!wk) return;

        try {
            await apiDelete(wk.weekNumber, wk.year);
            setSubmission(null);
            showSnack('Submission deleted.');
            await loadSubmission(wk.weekNumber, wk.year);
        } catch (err) {
            const message = err?.response?.data?.message || 'Failed to delete submission.';
            setError(message);
            showSnack(message, 'error');
        }
    }, [loadSubmission]);

    // ── Derived ──────────────────────────────────────────────────────────────
    // GetMyRole now returns: { wuRole: {..}|null, isPortalAdmin: bool, canManageUsers: bool }
    const wuRole         = role?.wuRole;
    const userRole       = wuRole?.role || wuRole?.Role || '';
    const isManager      = userRole === 'Manager';
    const isEditable     = deadline?.isEditable ?? false;
    const hasContentAccess = !!wuRole && !!userRole;
    const canManageUsers = role?.canManageUsers === true;
    const isPortalAdmin  = role?.isPortalAdmin === true;

    // User has access if they can see content OR manage users (Admin without WU role)
    const hasAccess = hasContentAccess || canManageUsers;

    // ── Render — single tree, no early returns ───────────────────────────────
    return (
        <Box sx={{ p: 3, mx: 'auto' }}>

            {/* Loading */}
            {loadingInit && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                        Loading Weekly Update...
                    </Typography>
                </Box>
            )}

            {/* No access */}
            {!loadingInit && !hasAccess && (
                <Alert severity="warning">
                    You do not have access to the Weekly Update module.
                    Please contact your manager to request access.
                </Alert>
            )}

            {/* Main content */}
            {!loadingInit && hasAccess && (
                <>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                        <Box>
                            <Typography variant="h6" fontWeight={500}>
                                Weekly Update
                            </Typography>
                            {hasContentAccess && (
                                <Typography variant="body2" color="text.secondary">
                                    {currentWeek && (
                                        <>
                                            Week {currentWeek.weekNumber}, {currentWeek.year}
                                            {' · '}
                                            <Chip
                                                label={isEditable ? 'Open for editing' : 'Locked'}
                                                size="small"
                                                color={isEditable ? 'success' : 'error'}
                                                variant="outlined"
                                                sx={{ height: 18, fontSize: '0.68rem', verticalAlign: 'middle' }}
                                                icon={isEditable ? undefined : <LockIcon sx={{ fontSize: 12 }} />}
                                            />
                                        </>
                                    )}
                                </Typography>
                            )}
                            {!hasContentAccess && isPortalAdmin && (
                                <Typography variant="body2" color="text.secondary">
                                    Admin access — user management only
                                </Typography>
                            )}
                            {deadline?.message && hasContentAccess && (
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                    {deadline.message}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {(loadingSub || saving) && <CircularProgress size={20} />}
                            {hasContentAccess && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<HistoryIcon />}
                                    onClick={() => setHistoryOpen(true)}
                                    sx={{ textTransform: 'none', fontSize: 12, borderRadius: 2 }}
                                >
                                    History
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Tabs — dynamic based on permissions */}
                    {(isManager || canManageUsers) && (
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
                        >
                            {hasContentAccess && <Tab label="My submission" sx={{ textTransform: 'none', fontSize: 13 }} />}
                            {isManager && <Tab label="Manager dashboard" sx={{ textTransform: 'none', fontSize: 13 }} />}
                            {canManageUsers && <Tab label="User management" sx={{ textTransform: 'none', fontSize: 13 }} />}
                        </Tabs>
                    )}

                    {/* Tab content */}
                    {(() => {
                        const panels = [];
                        if (hasContentAccess) panels.push('submission');
                        if (isManager) panels.push('dashboard');
                        if (canManageUsers) panels.push('management');
                        const activePanel = panels[tab] || panels[0];

                        return (
                            <>
                                {activePanel === 'submission' && currentWeek && (
                                    <WeeklyUpdateForm
                                        submission={submission}
                                        weekNumber={currentWeek.weekNumber}
                                        year={currentWeek.year}
                                        isEditable={isEditable}
                                        saving={saving}
                                        onSave={handleSave}
                                        onDelete={handleDelete}
                                        copiedData={copiedData}
                                        onCopiedDataConsumed={() => setCopiedData(null)}
                                    />
                                )}

                                {activePanel === 'dashboard' && (
                                    <ManagerDashboard />
                                )}

                                {activePanel === 'management' && (
                                    <UserManagement />
                                )}
                            </>
                        );
                    })()}
                </>
            )}

            {/* ── Submission History Drawer ───────────────────────────────── */}
            {currentWeek && (
                <SubmissionHistory
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    currentWeekNumber={currentWeek.weekNumber}
                    currentYear={currentWeek.year}
                    onCopyToCurrentWeek={handleCopyFromHistory}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};


// ─── Export ─────────────────────────────────────────────────────────────────

const WeeklyUpdate = () => <WeeklyUpdateInner />;

export default WeeklyUpdate;